"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useState, createContext, useContext } from "react";
import posthog from "posthog-js";
import { PostHogPageView } from "./posthog-page-view";
interface PostHogProviderProps {
  enabled: boolean;
  posthogKey?: string;
  posthogHost?: string;
  children?: React.ReactNode;
}
type EventProperties = {
  has_whitelable?: boolean;
  has_service?: boolean;
};
type PostHogContextValue = {
  eventProperties: EventProperties;
  setEventProperties: (properties: EventProperties) => void;
};
const PostHogContext = createContext<PostHogContextValue | undefined>(undefined);

/**
 * Hook to access PostHog event properties
 */
export function usePostHogEventProperties() {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error("usePostHogEventProperties must be used within a PostHogProvider");
  }
  return context;
}

/**
 * PostHog analytics provider that initializes PostHog on the client side
 * and tracks page views automatically.
 *
 * @param enabled - Runtime control to enable/disable PostHog
 * @param posthogKey - PostHog API key (from build time)
 * @param posthogHost - PostHog host URL (from build time)
 */
export function PostHogProvider(t0) {
  const $ = _c(15);
  const {
    enabled,
    posthogKey,
    posthogHost,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [eventProperties, setEventProperties] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== enabled || $[2] !== posthogHost || $[3] !== posthogKey) {
    t2 = () => {
      if (enabled && posthogKey) {
        posthog.init(posthogKey, {
          api_host: posthogHost ?? "https://app.posthog.com",
          person_profiles: "identified_only",
          capture_pageview: false,
          capture_pageleave: true,
          loaded: _temp
        });
      }
    };
    t3 = [enabled, posthogKey, posthogHost];
    $[1] = enabled;
    $[2] = posthogHost;
    $[3] = posthogKey;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== eventProperties) {
    t4 = {
      eventProperties,
      setEventProperties
    };
    $[6] = eventProperties;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== enabled || $[9] !== posthogKey) {
    t5 = <Suspense fallback={null}><PostHogPageView enabled={enabled} posthogKey={posthogKey} /></Suspense>;
    $[8] = enabled;
    $[9] = posthogKey;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== children || $[12] !== t4 || $[13] !== t5) {
    t6 = <PostHogContext.Provider value={t4}>{t5}{children}</PostHogContext.Provider>;
    $[11] = children;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp(posthog) {
  if (process.env.NODE_ENV === "development") {
    posthog.debug();
  }
}